import ModalShell from './ModalShell';
import { candidateDisplayName } from '../utils/candidateDisplay';
import {
  activeFactorPoints,
  boolLabel,
  FACTOR_STATUS_ICON,
  MATCH_WEIGHT_SUMMARY,
  orderedMatchFactors,
  overallPercent,
  skillsPercent,
  tierMeta
} from '../utils/matchBreakdown';

function FactorCard({ factor }) {
  const icon = FACTOR_STATUS_ICON[factor.status] || '—';
  const statusColors = {
    pass: 'border-green-200 bg-green-50/50',
    partial: 'border-amber-200 bg-amber-50/50',
    fail: 'border-red-200 bg-red-50/40',
    unknown: 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]',
    na: 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]'
  };
  const textColors = {
    pass: 'text-green-800',
    partial: 'text-amber-800',
    fail: 'text-red-800',
    unknown: 'text-[hsl(var(--ds-muted-fg))]',
    na: 'text-[hsl(var(--ds-muted-fg))]'
  };

  return (
    <div
      className={`border rounded-sm p-3 ${statusColors[factor.status] || statusColors.unknown}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex gap-2 min-w-0 flex-1">
          <span className={`text-base font-bold shrink-0 ${textColors[factor.status] || ''}`}>
            {icon}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{factor.label}</p>
              {factor.key === 'location' && factor.distanceKm != null ? (
                <span className="inline-flex items-center rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--ds-fg))] shadow-sm">
                  ~{factor.distanceKm} km
                </span>
              ) : null}
            </div>
            <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">{factor.summary}</p>
            {factor.detail ? <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">{factor.detail}</p> : null}
          </div>
        </div>
        <div className="text-right shrink-0 text-xs">
          {factor.applies ? (
            <>
              <p className="font-semibold text-[hsl(var(--ds-fg))]">{factor.percent}%</p>
              <p className="text-[hsl(var(--ds-muted-fg))]">
                {factor.pointsEarned}/{factor.pointsMax} pts · weight {factor.weight}%
              </p>
            </>
          ) : (
            <p className="text-[hsl(var(--ds-muted-fg))] italic">Not scored on job</p>
          )}
        </div>
      </div>
      {factor.applies && (
        <div className="mt-2 h-1.5 bg-[hsl(var(--ds-muted))] rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${
              factor.status === 'pass'
                ? 'bg-[hsl(var(--ds-success))]'
                : factor.status === 'partial'
                  ? 'bg-amber-500'
                  : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(100, factor.percent || 0)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function ChipList({ title, items, variant = 'default' }) {
  if (!items?.length) {
    return (
      <div>
        <p className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">{title}</p>
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">—</p>
      </div>
    );
  }
  const colors =
    variant === 'match'
      ? 'bg-green-100 text-green-900 border-green-200'
      : variant === 'missing'
        ? 'bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))] border-amber-200'
        : 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary)/0.25)]';
  return (
    <div>
      <p className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">{title}</p>
      <div className="flex flex-wrap gap-1">
        {items.map(item => (
          <span key={item} className={`text-[11px] px-2 py-0.5 border ${colors}`}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function MatchAnalyticsModal({
  matchRow,
  jobRequirements,
  methodology,
  modalTitle,
  modalSubtitle,
  onClose,
  onOpenProfile
}) {
  if (!matchRow) return null;

  const { candidate } = matchRow;
  const pct = overallPercent(matchRow);
  const skillPct = skillsPercent(matchRow);
  const tier = tierMeta(matchRow.relevanceTier);
  const factors = orderedMatchFactors(matchRow);
  const { earned, max, count } = activeFactorPoints(matchRow);
  const req = jobRequirements || {};

  const checks = [
    { label: 'Skills threshold', value: matchRow.requirementsMet, hint: 'Overall criteria bundle' },
    { label: 'Experience', value: matchRow.experienceMatch, hint: boolLabel(matchRow.experienceMatch) },
    { label: 'Education', value: matchRow.qualificationMatch, hint: boolLabel(matchRow.qualificationMatch) },
    {
      label: 'Location',
      value: matchRow.locationMatch,
      hint: matchRow.locationDistanceKm != null
        ? `${boolLabel(matchRow.locationMatch)} · ~${Math.round(matchRow.locationDistanceKm)} km`
        : matchRow.locationDetail || boolLabel(matchRow.locationMatch)
    },
    { label: 'Package / CTC', value: matchRow.packageMatch, hint: boolLabel(matchRow.packageMatch) },
    { label: 'Gender', value: matchRow.genderMatch, hint: boolLabel(matchRow.genderMatch) },
    { label: 'Age group', value: matchRow.ageGroupMatch, hint: boolLabel(matchRow.ageGroupMatch) },
    { label: 'Working mode', value: matchRow.workingModeMatch, hint: boolLabel(matchRow.workingModeMatch) }
  ];

  return (
    <ModalShell
      title={modalTitle || `Match analytics — ${candidateDisplayName(candidate)}`}
      subtitle={modalSubtitle || '360° profile fit vs job requirements'}
      onClose={onClose}
      maxWidth="max-w-3xl"
      zIndex="z-[13000]"
      footer={
        <>
          {onOpenProfile && candidate?.id && (
            <button
              type="button"
              className="px-4 py-2 text-sm border border-[hsl(var(--ds-primary)/0.35)] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] mr-auto"
              onClick={() => onOpenProfile(candidate.id)}
            >
              View & edit profile
            </button>
          )}
          <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs px-2.5 py-1 border font-medium ${tier.className}`}>{tier.label}</span>
          {matchRow.fitForSubmission && (
            <span className="text-xs px-2.5 py-1 bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border border-[hsl(var(--ds-primary)/0.25)]">
              Recommended to submit
            </span>
          )}
          {matchRow.requirementsMet === false && (
            <span className="text-xs px-2.5 py-1 bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))] border border-amber-200">
              Some requirements not met
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="border border-[hsl(var(--ds-border))] p-3 bg-[hsl(var(--ds-muted))]">
            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] uppercase">Overall fit</p>
            <p className="text-2xl font-bold text-[hsl(var(--ds-fg))]">{pct}%</p>
          </div>
          <div className="border border-[hsl(var(--ds-border))] p-3 bg-[hsl(var(--ds-muted))]">
            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] uppercase">Skills match</p>
            <p className="text-2xl font-bold text-green-800">{skillPct}%</p>
          </div>
          <div className="border border-[hsl(var(--ds-border))] p-3 bg-[hsl(var(--ds-muted))]">
            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] uppercase">Weighted score</p>
            <p className="text-2xl font-bold text-[hsl(var(--ds-fg))]">
              {earned}
              <span className="text-sm font-normal text-[hsl(var(--ds-muted-fg))]"> / {max || 100}</span>
            </p>
            <p className="text-[10px] text-[hsl(var(--ds-muted-fg))]">{count} active factors</p>
          </div>
          <div className="border border-[hsl(var(--ds-border))] p-3 bg-[hsl(var(--ds-muted))]">
            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] uppercase">Rank score</p>
            <p className="text-2xl font-bold text-[hsl(var(--ds-fg))]">{matchRow.rankScore ?? matchRow.score ?? '—'}</p>
          </div>
        </div>

        <div className="border border-[hsl(var(--ds-border))] p-3 bg-[hsl(var(--ds-surface))]">
          <p className="text-xs font-semibold text-[hsl(var(--ds-fg))] mb-2">Candidate profile</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Email</dt>
              <dd>{candidate.displayEmail || candidate.contact?.email || '—'}</dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Location</dt>
              <dd>{candidate.displayLocation || candidate.location || '—'}</dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Experience</dt>
              <dd>
                {candidate.displayExperience ||
                  (matchRow.candidateExperienceYears != null
                    ? `${matchRow.candidateExperienceYears} yrs`
                    : candidate.experienceYears != null
                      ? `${candidate.experienceYears} yrs`
                      : '—')}
              </dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Qualification</dt>
              <dd>{matchRow.candidateQualification || candidate.educationalQualification || '—'}</dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Expected CTC</dt>
              <dd>
                {candidate.expectedCtc != null
                  ? `${candidate.expectedCtc} LPA`
                  : candidate.package || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Working mode</dt>
              <dd>{(candidate.preferredWorkingModes || []).join(', ') || '—'}</dd>
            </div>
          </dl>
        </div>

        <div className="border border-[hsl(var(--ds-border))] p-3 bg-[hsl(var(--ds-muted))]">
          <p className="text-xs font-semibold text-[hsl(var(--ds-fg))] mb-2">Job requirements (this role)</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Experience</dt>
              <dd>
                {req.experienceFrom || req.experienceTo
                  ? `${req.experienceFrom || '—'} – ${req.experienceTo || '—'} yrs`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Qualification</dt>
              <dd>
                {(req.educationalQualifications?.length
                  ? req.educationalQualifications
                  : req.educationalQualification
                    ? [req.educationalQualification]
                    : []
                ).join('; ') || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Location</dt>
              <dd>{req.location || '—'}</dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Budget</dt>
              <dd>
                {req.budgetFrom || req.budgetTo
                  ? `${req.budgetFrom || '—'} – ${req.budgetTo || '—'} LPA`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Gender</dt>
              <dd>{req.preferredGender || 'Any'}</dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--ds-muted-fg))]">Age group</dt>
              <dd>{(req.ageGroups?.length ? req.ageGroups : req.ageGroup ? [req.ageGroup] : []).join(', ') || '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-[hsl(var(--ds-muted-fg))]">Working mode</dt>
              <dd>{(req.preferredWorkingModes || []).join(', ') || '—'}</dd>
            </div>
          </dl>
        </div>

        <div>
          <p className="text-xs font-semibold text-[hsl(var(--ds-fg))] mb-2">Skills alignment</p>
          <p className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Core Competencies</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border border-[hsl(var(--ds-border))] p-3 mb-3">
            <ChipList title="Must-have matched" items={matchRow.mustMatched} variant="match" />
            <ChipList title="Must-have missing" items={matchRow.mustMissing} variant="missing" />
            <ChipList title="Nice-to-have matched" items={matchRow.niceMatched} variant="nice" />
          </div>
          <p className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Technical Skills</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border border-[hsl(var(--ds-border))] p-3">
            <ChipList title="Must-have matched" items={matchRow.techMustMatched} variant="match" />
            <ChipList title="Must-have missing" items={matchRow.techMustMissing} variant="missing" />
            <ChipList title="Nice-to-have matched" items={matchRow.techNiceMatched} variant="nice" />
          </div>
          {(req.mustHaveSkills?.length > 0 ||
            req.niceToHaveSkills?.length > 0 ||
            req.mustHaveTechnicalSkills?.length > 0 ||
            req.niceToHaveTechnicalSkills?.length > 0) && (
            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-2">
              Job lists {req.mustHaveSkills?.length || 0} Core Competencies (must-have),{' '}
              {req.niceToHaveSkills?.length || 0} Core Competencies (nice-to-have),{' '}
              {req.mustHaveTechnicalSkills?.length || 0} Technical Skills (must-have), and{' '}
              {req.niceToHaveTechnicalSkills?.length || 0} Technical Skills (nice-to-have).
            </p>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold text-[hsl(var(--ds-fg))] mb-1">Factor-by-factor breakdown</p>
          <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mb-3">
            {methodology?.summary || MATCH_WEIGHT_SUMMARY}
          </p>
          <div className="space-y-2">
            {factors.map(f => (
              <FactorCard key={f.key} factor={f} />
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-[hsl(var(--ds-fg))] mb-2">Requirement checks</p>
          <div className="border border-[hsl(var(--ds-border))] divide-y divide-[hsl(var(--ds-border))]">
            {checks.map(row => (
              <div key={row.label} className="flex items-center justify-between px-3 py-2 text-xs">
                <span className="text-[hsl(var(--ds-fg))]">{row.label}</span>
                <span
                  className={
                    row.value === true
                      ? 'text-green-700 font-medium'
                      : row.value === false
                        ? 'text-red-700 font-medium'
                        : 'text-[hsl(var(--ds-muted-fg))]'
                  }
                >
                  {row.hint}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
